import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import ContentCard from "../components/ContentCard";

const CATS = ["all", "video", "presentation", "other"];

export default function Library() {
  const { category } = useParams();
  const { t, lang, translateBatch } = useLang();
  const [active, setActive] = useState(category || "all");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [sortMode, setSortMode] = useState("default");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setActive(category || "all");
    setActiveTag(null);
  }, [category]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/content").then(async (res) => {
      let data = res.data;
      if (lang === "it" && data.length) {
        const titles = await translateBatch(data.map((d) => d.title));
        const descs = await translateBatch(data.map((d) => d.description || ""));
        data = data.map((d, i) => ({ ...d, title: titles[i], description: descs[i] }));
      }
      if (alive) { setItems(data); setLoading(false); }
    });
    return () => { alive = false; };
  }, [lang, translateBatch]);

  const catLabel = (c) =>
    c === "all" ? t("all") : c === "video" ? t("video") : c === "presentation" ? t("presentations") : t("other");

  const inCategory = items.filter((i) => (active === "all" ? true : i.category === active));
  const availableTags = Array.from(new Set(inCategory.flatMap((i) => i.tags || []))).sort();

  const filtered = inCategory
    .filter((i) => {
      const ql = q.toLowerCase();
      return (
        i.title.toLowerCase().includes(ql) ||
        (i.tags || []).some((tag) => tag.toLowerCase().includes(ql)) ||
        (i.speaker || "").toLowerCase().includes(ql)
      );
    })
    .filter((i) => (activeTag ? (i.tags || []).includes(activeTag) : true));

  const sorted = (() => {
    if (sortMode === "recent") {
      return [...filtered].sort((a, b) => {
        const da = new Date(a.recorded_at || a.created_at || 0).getTime();
        const db = new Date(b.recorded_at || b.created_at || 0).getTime();
        return db - da;
      });
    }
    if (sortMode === "views") {
      return [...filtered].sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    if (sortMode === "az") {
      return [...filtered].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
      );
    }
    return filtered; // "default" — server order (featured -> manual order -> title)
  })();

  return (
    <div data-testid="library-page" className="min-h-screen px-6 pt-32 pb-28 md:px-10">
      <div className="mx-auto max-w-[1600px]">
        <div className="grid-lines absolute inset-x-0 top-0 h-96 opacity-40" style={{ zIndex: -1 }} />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan">— {t("browse_library")}</p>
          <h1 className="mt-4 font-head text-5xl font-black uppercase tracking-tighter text-white md:text-7xl">
            {t("library")}
          </h1>
          <p className="mt-4 max-w-xl font-body text-slate-400">{t("library_sub")}</p>
        </motion.div>

        {/* Controls */}
        <div className="mt-12 flex flex-col gap-6 border-y border-white/10 py-6 md:flex-row md:flex-wrap md:items-center md:gap-8">
          <div className="flex flex-wrap gap-3">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setActive(c)}
                className="border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300"
                style={{
                  borderColor: active === c ? "#00F0FF" : "rgba(255,255,255,0.15)",
                  backgroundColor: active === c ? "#00F0FF" : "transparent",
                  color: active === c ? "#050810" : "#94A3B8",
                }}
              >
                {catLabel(c)}
              </button>
            ))}
          </div>
          <input
            data-testid="library-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search_placeholder")}
            className="w-full max-w-xs border-b border-white/20 bg-transparent py-2 font-body text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-cyan"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("sort_by")}:</span>
            {[
              { key: "default", label: t("sort_default") },
              { key: "recent", label: t("sort_recent") },
              { key: "views", label: t("sort_views") },
              { key: "az", label: t("sort_az") },
            ].map((opt) => {
              const on = sortMode === opt.key;
              return (
                <button
                  key={opt.key}
                  data-testid={`sort-${opt.key}`}
                  onClick={() => setSortMode(opt.key)}
                  className="border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors"
                  style={{
                    borderColor: on ? "#00F0FF" : "rgba(255,255,255,0.15)",
                    backgroundColor: on ? "rgba(0,240,255,0.12)" : "transparent",
                    color: on ? "#00F0FF" : "#94A3B8",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tag / technique filters */}
        {availableTags.length > 0 && (
          <div data-testid="tag-filter-bar" className="mt-6 flex flex-wrap items-center gap-2">
            <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("topics")}:</span>
            <button
              data-testid="tag-all"
              onClick={() => setActiveTag(null)}
              className="rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors"
              style={{ borderColor: !activeTag ? "#00F0FF" : "rgba(255,255,255,0.15)", color: !activeTag ? "#00F0FF" : "#94A3B8" }}
            >
              {t("all")}
            </button>
            {availableTags.map((tg) => (
              <button
                key={tg}
                data-testid={`tag-${tg}`}
                onClick={() => setActiveTag(activeTag === tg ? null : tg)}
                className="rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors"
                style={{ borderColor: activeTag === tg ? "#00F0FF" : "rgba(255,255,255,0.15)", backgroundColor: activeTag === tg ? "rgba(0,240,255,0.12)" : "transparent", color: activeTag === tg ? "#00F0FF" : "#94A3B8" }}
              >
                {tg}
              </button>
            ))}
          </div>
        )}

        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
          {sorted.length} {sorted.length === 1 ? "item" : "items"}
        </p>

        {loading ? (
          <p className="mt-16 font-mono text-sm text-slate-500">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="mt-16 font-body text-slate-500">{t("empty_section")}</p>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sorted.map((item, i) => (
              <ContentCard key={item.id} item={item} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
